import PropTypes from 'prop-types';
import React, { Component } from 'react';

import { UX2, utils as CoreUtils } from '@wsb/guac-widget-core';
const {
  postRender: { Actions: postRenderActions, monitor, onWidgetRendered }
} = CoreUtils;

import Agenda from './agenda';
import config from '../config';
import validationRegex from './util';
import getLocale from './getLocale';
import { generateDefaultEvents } from './defaultEvents';

const DEFAULT_ICAL_URL =
  'https://calendar.google.com/calendar/ical/wsb.vnext%40gmail.com/public/basic.ics';
const CACHE_TTL = 300 * 1000; // ms to cache calendar event results

class Calendar extends Component {
  static get propTypes() {
    return {
      env: PropTypes.string,
      icalURL: PropTypes.string,
      websiteId: PropTypes.string,
      pageId: PropTypes.string,
      locale: PropTypes.string,
      id: PropTypes.string,
      anchorId: PropTypes.string,
      renderMode: PropTypes.string,
      visitorMode: PropTypes.bool,
      detachedMode: PropTypes.bool,
      staticContent: PropTypes.object.isRequired,
      isManual: PropTypes.bool.isRequired,
      manualEvents: PropTypes.arrayOf(PropTypes.object).isRequired,
      timezone: PropTypes.object
    };
  }

  constructor(props) {
    super(props);
    this.regex = new RegExp(validationRegex, 'i');
    this.state = {
      linkedEvents: [],
      loading: true
    };
  }

  componentDidMount() {
    const { id, isManual } = this.props;
    this._isMounted = true;
    if (!isManual) {
      monitor(id, postRenderActions.SCROLL_TO_URL_FRAGMENT);
      this.getEvents();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.loading && !this.state.loading) {
      onWidgetRendered(this.props.id, postRenderActions.SCROLL_TO_URL_FRAGMENT);
    }

    if (!this.regex.test(this.props.icalURL)) {
      // ical is not valid
      return;
    }

    const urlChanged = prevProps.icalURL !== this.props.icalURL;
    const manualToLinked = prevProps.isManual && !this.props.isManual;
    if (urlChanged || manualToLinked) {
      this.getEvents(this.props.icalURL);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchEvents(directURL) {
    const { env, websiteId, pageId, id } = this.props;

    const envConfig = (config && config[env]) || {};
    let url = `${envConfig.calendarDomain}/v1/events/${websiteId}/${pageId}/${id}`;
    if (directURL) {
      url += `?icalURL=${encodeURIComponent(directURL)}`;
      url += `?t=${Math.floor(new Date().getTime() / CACHE_TTL)}`;
    }

    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.send();
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4 && this._isMounted) {
        this.setState({
          linkedEvents: xhr.status === 200 ? JSON.parse(xhr.responseText).events : [],
          loading: false
        });
      }
    };
  }

  shouldGenerateEventsLocally(directURL) {
    const { detachedMode, icalURL, staticContent } = this.props;
    if (!staticContent.defaultEvents) {
      return false;
    }
    return (
      detachedMode || directURL === DEFAULT_ICAL_URL || (!directURL && icalURL === DEFAULT_ICAL_URL)
    );
  }

  getEvents(directURL) {
    const { staticContent } = this.props;

    if (!this._isMounted) {
      return;
    }
    this.setState({ loading: true });

    if (this.shouldGenerateEventsLocally(directURL)) {
      this._isMounted &&
        this.setState({
          linkedEvents: generateDefaultEvents(staticContent),
          loading: false
        });
      return;
    }

    this.fetchEvents(directURL);
  }

  render() {
    const { anchorId, id, visitorMode, staticContent, locale, renderMode, isManual, timezone } =
      this.props;
    if (!isManual && (this.state.loading || typeof window === 'undefined')) {
      return (
        <UX2.Component.Grid bottom={ false } style={{ paddingVertical: 'large', minHeight: '100vh' }}>
          <UX2.Component.Grid.Cell style={{ textAlign: 'center' }}>
            <UX2.Element.Loader size='medium' />
          </UX2.Component.Grid.Cell>
        </UX2.Component.Grid>
      );
    }

    const eventsType = isManual ? this.props.manualEvents : this.state.linkedEvents;
    return (
      <Agenda
        events={ eventsType }
        anchorId={ anchorId }
        id={ id }
        renderMode={ renderMode }
        visitorMode={ visitorMode }
        staticContent={ staticContent }
        locale={ getLocale(locale) }
        isManual={ isManual }
        timezone={ timezone }
      />
    );
  }
}

export default Calendar;
